import { tokens } from './tokens';

export type TxType = 'buy' | 'sell' | 'tp' | 'sl';

/**
 * Fields shown in the transaction summary panel. The closed row, the expanded
 * row and the MRC deep-dive row all render these identically (via SummaryPanel)
 * so the values never shift or drift between views.
 */
export interface SummaryTx {
  id: number;
  type: TxType;
  orderType: 'BUY' | 'SELL';
  date: string;
  price: string;
  profit: string;
  /** Fraction (0..1) of the balance held in stable at this transaction. */
  stableShare: number;
}

export interface TxItem extends SummaryTx {
  timestamp: number;
  stableQty: string;
  stableBal: string;
  assetQty: string;
  assetBal: string;
  fee: string;
  totalFees: string;
}

export interface DeepTx extends SummaryTx {
  stableQty: string;
  stableProfit: string;
  assetQty: string;
  assetProfit: string;
  stableBal: string;
  assetBal: string;
  fee: string;
  totalFees: string;
  /** Type-specific stat for QUANTITIES & BALANCES — take-profit size for a
   * take-profit, stop-loss threshold for a stop-loss, null for core. */
  extraStat: { label: string; value: string } | null;
}

export interface MrcPhase {
  name: 'INITIALISED' | 'PENDING' | 'RESET' | 'CANCELLED' | 'SWITCH';
  color: string;
  /** Timeframe this phase was evaluated on (e.g. '4h'). */
  timeFrame: string;
  startDate: string;
  endDate: string;
  startPrice: string;
  endPrice: string;
  startRsi: string;
  endRsi: string;
}

export const txColor = (type: TxType): string =>
  ({
    sell: tokens.red,
    buy: tokens.green,
    tp: tokens.yellow,
    sl: tokens.blue,
  }[type] || tokens.textMuted);

export const txLabel = (type: TxType): string =>
  ({
    sell: 'Core transaction',
    buy: 'Core transaction',
    tp: 'Take Profit',
    sl: 'Stop Loss',
  }[type] || type);

export const txDirection = (tx: {
  orderType: 'BUY' | 'SELL';
}): 'BUY' | 'SELL' => tx.orderType;

/** Colour for the BUY/SELL direction label — green buy, red sell. */
export const txDirectionColor = (tx: { orderType: 'BUY' | 'SELL' }): string =>
  tx.orderType === 'BUY' ? tokens.green : tokens.red;

export const txIsCore = (type: TxType): boolean =>
  type === 'sell' || type === 'buy';
